/**
 * Authentication Interceptor
 *
 * Attaches JWT bearer tokens to outgoing HTTP requests and refreshes expired
 * access tokens before logging the user out.
 */

import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { HttpStatus, RoutePaths } from '../constants';
import { environment } from '../../../environments/environment';

/** Set this context to true on a request to force no Authorization header (e.g. signup/register). */
export const SKIP_AUTH = new HttpContextToken<boolean>(() => false);

/** Public endpoints that must never receive an Authorization header */
const PUBLIC_ENDPOINTS = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/refresh-token',
  '/api/v1/auth/login',
  '/api/v1/auth/register',
  '/api/v1/auth/refresh',
  '/api/v1/auth/forgot-password',
  '/api/v1/auth/reset-password',
  '/api/v1/auth/resend-verification-email',
  '/api/v1/auth/verify-email',
  '/api/v1/auth/magic-link',
  '/api/v1/auth/magic-link/verify',
  '/api/v1/customer-profiles/register',
  'customer-profiles/register',
  '/api/v1/industries',
  '/api/v1/companies'
] as const;

const isPublicEndpoint = (req: { url: string }): boolean => {
  const url = req.url.toLowerCase();
  return PUBLIC_ENDPOINTS.some(endpoint => url.includes(endpoint.toLowerCase()));
};

const shouldLogDebug = (): boolean =>
  !environment.production && environment.logging?.enableConsoleLogging === true;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const skipAuth = req.context.get(SKIP_AUTH);
  const isPublic = skipAuth || isPublicEndpoint(req);

  if (shouldLogDebug()) {
    console.debug(
      '[AuthInterceptor]',
      req.method,
      req.url,
      'isPublic:',
      isPublic,
      'hasAuth:',
      req.headers.has('Authorization')
    );
  }

  if (isPublic) {
    return next(req.clone({ headers: req.headers.delete('Authorization') }));
  }

  const token = authService.getToken();
  const authReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (
        error.status !== HttpStatus.UNAUTHORIZED ||
        isPublic
      ) {
        return throwError(() => error);
      }

      return authService.refreshAccessToken().pipe(
        switchMap((newToken) =>
          next(
            req.clone({
              setHeaders: { Authorization: `Bearer ${newToken}` }
            })
          )
        ),
        catchError((refreshError) => {
          authService.logout();
          router.navigate([RoutePaths.LOGIN], {
            queryParams: { returnUrl: router.url, sessionExpired: 'true' }
          });
          return throwError(() => refreshError);
        })
      );
    })
  );
};
